import React, { useState } from 'react';
import { Drawer, Empty, Tooltip } from 'antd';
import {
    BellOutlined,
    CameraOutlined,
    CarOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    ExperimentOutlined,
    FireOutlined,
    SafetyCertificateOutlined,
    ShoppingOutlined,
    UserOutlined,
} from '@ant-design/icons';

import { DETECTION_TYPES } from './analyticsData';

const TYPE_ICONS = {
    'Face Recognition': <UserOutlined />,
    'Number Plate': <CarOutlined />,
    'Baggage Detection': <ShoppingOutlined />,
    'PPE Detection': <SafetyCertificateOutlined />,
    'FireSmoke Detection': <FireOutlined />,
    'ForeignMaterial Detection': <ExperimentOutlined />,
};

// Alerts reuse the KPI/chart palette so a card reads as the same detection class
// the user just saw in the cards above.
const TYPE_COLORS = DETECTION_TYPES.reduce(
    (acc, type) => ({ ...acc, [type.key]: type.color }),
    {}
);

const FALLBACK_COLOR = '#8c8c8c';

const AlertRow = ({ alert, onResolve }) => {
    const color = TYPE_COLORS[alert.kpiType] || FALLBACK_COLOR;
    return (
        <div
            className={`analytics-alert-card ${onResolve ? '' : 'is-resolved'}`}
            style={{ borderLeftColor: color, backgroundColor: `${color}0D` }}
        >
            <div
                className='analytics-alert-icon'
                style={{ backgroundColor: `${color}1A`, color }}
            >
                {TYPE_ICONS[alert.kpiType]}
            </div>
            <div className='analytics-alert-body'>
                <div className='analytics-alert-title' style={{ color }}>{alert.title}</div>
                <div className='analytics-alert-meta'>
                    <span><CameraOutlined /> {alert.cameraName}</span>
                    <span><ClockCircleOutlined /> {alert.at ? alert.at.fromNow() : 'Unknown time'}</span>
                </div>
                <div className='analytics-alert-detail'>
                    {alert.zoneName} · {alert.detectionName || 'No detection name'}
                </div>
            </div>
            {onResolve && (
                <Tooltip title='Mark as resolved'>
                    <button
                        type='button'
                        className='analytics-alert-resolve'
                        onClick={() => onResolve(alert.id)}
                    >
                        <CheckOutlined />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

/**
 * Alerts live entirely in component state, so anything the user resolves during the
 * session reverts to the defaults built from the CSV on the next page load.
 */
const AlertsPanel = ({ alerts }) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('active');
    const [resolvedIds, setResolvedIds] = useState([]);

    const active = alerts.active.filter((alert) => !resolvedIds.includes(alert.id));
    const resolved = [
        ...alerts.active.filter((alert) => resolvedIds.includes(alert.id)),
        ...alerts.resolved,
    ];

    const resolve = (id) => setResolvedIds((ids) => [...ids, id]);

    const showing = tab === 'active' ? active : resolved;

    return (
        <>
            <Tooltip title='Alerts'>
                <button
                    type='button'
                    className='analytics-alert-trigger'
                    onClick={() => setOpen(true)}
                >
                    <BellOutlined />
                    {active.length > 0 && (
                        <span className='analytics-alert-badge'>{active.length}</span>
                    )}
                </button>
            </Tooltip>

            <Drawer
                title={<span className='analytics-alert-drawer-title'><BellOutlined /> Alerts</span>}
                placement='right'
                width={380}
                onClose={() => setOpen(false)}
                visible={open}
                bodyStyle={{ padding: '12px 16px 24px' }}
            >
                <div className='analytics-alert-tabs'>
                    <button
                        type='button'
                        className={`analytics-alert-tab ${tab === 'active' ? 'is-active' : ''}`}
                        onClick={() => setTab('active')}
                    >
                        Active ({active.length})
                    </button>
                    <button
                        type='button'
                        className={`analytics-alert-tab ${tab === 'resolved' ? 'is-active' : ''}`}
                        onClick={() => setTab('resolved')}
                    >
                        Resolved ({resolved.length})
                    </button>
                </div>

                <div className='analytics-alert-list'>
                    {showing.length ? (
                        showing.map((alert) => (
                            <AlertRow
                                key={alert.id}
                                alert={alert}
                                onResolve={tab === 'active' ? resolve : undefined}
                            />
                        ))
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={tab === 'active' ? 'No active alerts' : 'No resolved alerts'}
                        />
                    )}
                </div>
            </Drawer>
        </>
    );
};

export default AlertsPanel;
